import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './AiUsageTable.module.css';
import { getAuthToken } from '../../lib/auth';
import { HOST } from '../../config/api';
import { getAiModelLabel } from './aiModels';

export const AI_USAGE_PAGE_SIZE = 10;

const toDateInputValue = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const getCurrentMonthRange = (now = new Date()) => ({
    from: toDateInputValue(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: toDateInputValue(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
});

const formatUsageDate = (value) => {
    if (!value) {
        return '—';
    }

    const parsed = new Date(String(value).replace(' ', 'T'));
    if (Number.isNaN(parsed.getTime())) {
        return String(value);
    }

    return parsed.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const formatUsagePrice = (price, currency) => {
    const amount = Number(price) || 0;

    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'USD',
        }).format(amount);
    } catch (error) {
        return `${amount.toFixed(2)} ${currency || 'USD'}`;
    }
};

const buildDemoUsage = () => {
    const now = new Date();
    const models = ['gpt-5-mini', 'gpt-4.1-mini'];

    return Array.from({ length: 13 }, (_, index) => {
        const day = Math.max(1, now.getDate() - index);
        const date = new Date(now.getFullYear(), now.getMonth(), day, 9 + (index % 8), 15, 0);

        return {
            date: `${toDateInputValue(date)} ${String(date.getHours()).padStart(2, '0')}:15:00`,
            model: models[index % models.length],
            status: 'completed',
            currency: 'USD',
            estimatedPrice: Math.round((0.42 + index * 0.31) * 100) / 100,
        };
    });
};

const AiUsageTable = ({ userId, demoMode = false }) => {
    const defaultRange = useMemo(() => getCurrentMonthRange(), []);
    const [fromDate, setFromDate] = useState(defaultRange.from);
    const [toDate, setToDate] = useState(defaultRange.to);
    const [page, setPage] = useState(1);
    const [items, setItems] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [loading, setLoading] = useState(!demoMode);
    const [error, setError] = useState('');

    const navigate = useNavigate();
    const demoUsage = useMemo(() => (demoMode ? buildDemoUsage() : []), [demoMode]);
    const invalidRange = Boolean(fromDate && toDate && fromDate > toDate);

    useEffect(() => {
        if (demoMode || !fromDate || !toDate || invalidRange) {
            return undefined;
        }

        let cancelled = false;

        const loadUsage = async () => {
            setLoading(true);
            setError('');

            try {
                const params = new URLSearchParams({
                    userId: String(userId),
                    from: fromDate,
                    to: toDate,
                    page: String(page),
                    pageSize: String(AI_USAGE_PAGE_SIZE),
                });

                const response = await fetch(`${HOST}/v1/ai_usage?${params.toString()}`, {
                    headers: {
                        Authorization: `Bearer ${getAuthToken()}`,
                    },
                });

                if (response.status === 401) {
                    navigate('/login');
                    return;
                }

                if (!response.ok) {
                    throw new Error(`AI usage request failed: ${response.status}`);
                }

                const data = await response.json();
                if (cancelled) {
                    return;
                }

                setItems(Array.isArray(data.items) ? data.items : []);
                setPagination(data.pagination || null);

                const serverPage = data.pagination?.page;
                if (serverPage && serverPage !== page) {
                    setPage(serverPage);
                }
            } catch (fetchError) {
                if (!cancelled) {
                    console.error('Failed to load AI usage:', fetchError);
                    setItems([]);
                    setPagination(null);
                    setError('AI usage could not be loaded. Please try again.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadUsage();

        return () => {
            cancelled = true;
        };
    }, [demoMode, userId, fromDate, toDate, page, invalidRange, navigate]);

    const demoFiltered = useMemo(() => {
        if (!demoMode) {
            return [];
        }

        return demoUsage.filter((row) => {
            const rowDate = row.date.slice(0, 10);
            return (!fromDate || rowDate >= fromDate) && (!toDate || rowDate <= toDate);
        });
    }, [demoMode, demoUsage, fromDate, toDate]);

    const demoTotalPages = demoMode ? Math.ceil(demoFiltered.length / AI_USAGE_PAGE_SIZE) : 0;

    const rows = demoMode
        ? demoFiltered.slice((page - 1) * AI_USAGE_PAGE_SIZE, page * AI_USAGE_PAGE_SIZE)
        : items;
    const totalPages = demoMode ? demoTotalPages : pagination?.totalPages || 0;
    const totalItems = demoMode ? demoFiltered.length : pagination?.totalItems || 0;
    const showLoading = !demoMode && loading;

    const handleRangeChange = (setter) => (event) => {
        setter(event.target.value);
        setPage(1);
        setError('');
    };

    return (
        <section
            className={styles.usagePanel}
            aria-label="AI usage"
            onKeyDown={(event) => {
                // The panel can be rendered inside the coach profile form;
                // pressing Enter in a date field must not submit that form.
                if (event.key === 'Enter' && event.target.tagName === 'INPUT') {
                    event.preventDefault();
                }
            }}
        >
            <div className={styles.usageHeader}>
                <h4>AI usage</h4>
                <p>
                    Your AI training generations and their estimated price for the selected period.
                    Prices include the service fee and are rounded to the nearest cent.
                </p>
            </div>

            <div className={styles.filters}>
                <label className={styles.filterField}>
                    <span>From</span>
                    <input
                        type="date"
                        aria-label="AI usage from date"
                        value={fromDate}
                        max={toDate || undefined}
                        onChange={handleRangeChange(setFromDate)}
                    />
                </label>
                <label className={styles.filterField}>
                    <span>To</span>
                    <input
                        type="date"
                        aria-label="AI usage to date"
                        value={toDate}
                        min={fromDate || undefined}
                        onChange={handleRangeChange(setToDate)}
                    />
                </label>
            </div>

            {invalidRange ? (
                <p className={styles.usageError}>The from date must not be after the to date.</p>
            ) : null}

            {error ? <p className={styles.usageError}>{error}</p> : null}

            {showLoading ? <p className={styles.usageState}>Loading AI usage...</p> : null}

            {!showLoading && !invalidRange && !error && rows.length === 0 ? (
                <p className={styles.usageState}>No AI generations in the selected period.</p>
            ) : null}

            {!showLoading && !invalidRange && rows.length > 0 ? (
                <>
                    <div className={styles.tableWrap}>
                        <table className={styles.usageTable}>
                            <thead>
                                <tr>
                                    <th scope="col">Date</th>
                                    <th scope="col">Model</th>
                                    <th scope="col" className={styles.priceColumn}>Estimated price</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, index) => (
                                    <tr key={`${row.date}-${index}`}>
                                        <td>{formatUsageDate(row.date)}</td>
                                        <td>{row.model ? getAiModelLabel(row.model) : '—'}</td>
                                        <td className={styles.priceColumn}>
                                            {formatUsagePrice(row.estimatedPrice, row.currency)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className={styles.paginationRow}>
                        <button
                            type="button"
                            className={styles.pageButton}
                            onClick={() => setPage((value) => Math.max(1, value - 1))}
                            disabled={page <= 1}
                        >
                            Previous
                        </button>
                        <span className={styles.pageInfo}>
                            {`Page ${page} of ${Math.max(totalPages, 1)} (${totalItems} generation${totalItems === 1 ? '' : 's'})`}
                        </span>
                        <button
                            type="button"
                            className={styles.pageButton}
                            onClick={() => setPage((value) => value + 1)}
                            disabled={totalPages === 0 || page >= totalPages}
                        >
                            Next
                        </button>
                    </div>
                </>
            ) : null}
        </section>
    );
};

export default AiUsageTable;
